/**
 * Product Card Management System - Moon Moon Agency v1.0
 * Handles variant selection and quantity controls for product cards
 * Uses event delegation pattern consistent with mm_main_product.js
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION
 */
const CARD_CONFIG = {
    selectors: {
        cardForm: '[data-type="add-to-cart-form"]',
        variantInput: '[data-card-variant-input]',
        variantIdInput: '[data-variant-id-input]',
        quantityInput: '[data-card-qtty-input]',
        quantityIncrease: '[data-card-qtty-increase]',
        quantityDecrease: '[data-card-qtty-decrease]',
        submitButton: '.mm-product-cta',
        productCard: '.mm-product-card',
        openVariantPanel: '[data-open-variant-panel]',
        variantPanel: '[data-variant-panel]'
    },

};

/*********************************************************************************************
 * 📦 PRODUCT DATA MANAGEMENT
 */
const ProductCardData = {
    /**
     * Get product data from a specific card element
     * @param {HTMLElement} card - The card element containing the product data
     */
    getFromCard(card) {
        if (!card) {
            window.log('error', 'ProductCard', 'No card element provided');
            return null;
        }

        // Find the script tag within this specific card
        const scriptTag = card.querySelector('script[data-product-id]');
        if (!scriptTag) {
            window.log('error', 'ProductCard', 'No product data script found in card');
            return null;
        }

        try {
            const data = JSON.parse(scriptTag.textContent);

            // Validate required properties
            if (!data || typeof data.id === 'undefined' || !Array.isArray(data.variants)) {
                window.log('error', 'ProductCard', 'Invalid product data structure', { data });
                return null;
            }

            window.log('info', 'ProductCard', 'Product data loaded', {
                productId: data.id,
                variantsCount: data.variants.length
            });
            return data;
        } catch (e) {
            window.log('error', 'ProductCard', 'Failed to parse product data', { error: e.message });
            return null;
        }
    }
};

/*********************************************************************************************
 * 🔄 VARIANT MANAGEMENT
 */
const VariantManager = {
    findMatchingVariant(productData, selectedOptions) {
        if (!productData || !productData.variants) {
            window.log('error', 'ProductCard', 'Invalid product data');
            return null;
        }

        const matchedVariant = productData.variants.find(variant => {
            // Check each option position
            return Object.keys(selectedOptions).every(optionKey => {
                const selectedValue = selectedOptions[optionKey];
                const variantValue = variant[optionKey];
                return variantValue === selectedValue;
            });
        });

        if (!matchedVariant) {
            window.log('warn', 'ProductCard', 'No variant matched', {
                selectedOptions,
                availableVariants: productData.variants.map(v => ({
                    option1: v.option1,
                    option2: v.option2,
                    option3: v.option3
                }))
            });
        }

        return matchedVariant;
    },

    getSelectedOptions(form) {
        const options = {};
        const inputs = form.querySelectorAll(CARD_CONFIG.selectors.variantInput);

        window.log('info', 'ProductCard', 'Getting selected options', {
            totalInputs: inputs.length,
            formId: form.getAttribute('id')
        });

        inputs.forEach(input => {
            const isSelect = input.tagName === 'SELECT';
            const isSelected = isSelect ? input.value !== '' : input.checked;
            if (isSelected) {
                const position = input.dataset.optionPosition;
                const optionKey = `option${position}`;
                options[optionKey] = input.value;

                window.log('info', 'ProductCard', 'Found checked option', {
                    position,
                    optionKey,
                    value: input.value
                });
            }
        });

        return options;
    },

    updateVariantId(form, variantId) {
        const input = form.querySelector(CARD_CONFIG.selectors.variantIdInput);
        if (input) {
            const oldValue = input.value;
            input.value = variantId;
            window.log('info', 'ProductCard', 'Variant ID updated in hidden input', {
                oldValue,
                newValue: variantId,
                inputName: input.name,
                inputId: input.id
            });
        } else {
            window.log('error', 'ProductCard', 'Hidden variant input not found', {
                selector: CARD_CONFIG.selectors.variantIdInput
            });
        }
    },

    updateButton(form, variant) {
        const button = form.querySelector(CARD_CONFIG.selectors.submitButton);
        if (!button) return;

        // Use texts from data attributes (set by Liquid translations)
        const addText = button.dataset.addText;
        const soldOutText = button.dataset.soldOutText;

        if (!addText || !soldOutText) {
            window.log('warn', 'ProductCard', 'Button missing translation data attributes', {
                hasAddText: !!addText,
                hasSoldOutText: !!soldOutText
            });
            return;
        }

        if (variant.available) {
            button.disabled = false;
            button.classList.remove('mm-cursor-default');
            button.textContent = addText;
        } else {
            button.disabled = true;
            button.classList.add('mm-cursor-default');
            button.textContent = soldOutText;
        }
    },

    updateVariantImage(form, variant) {
        if (!variant.featured_image) {
            window.log('info', 'ProductCard', 'No featured image for variant', { variantId: variant.id });
            return;
        }

        const card = form.closest(CARD_CONFIG.selectors.productCard);
        if (!card) return;

        // Find the main image in the card (not the hover image)
        const mainImage = card.querySelector('.mm-product-card-image:not(.mm-product-card-image-hover)');
        if (!mainImage) {
            window.log('warn', 'ProductCard', 'Main product image not found in card');
            return;
        }

        try {
            // featured_image is a URL string from the JSON data
            const imageUrl = typeof variant.featured_image === 'string'
                ? variant.featured_image
                : variant.featured_image.src;
            mainImage.src = imageUrl;
            mainImage.alt = variant.name || mainImage.alt;

            // Update srcset if present (for responsive images)
            if (mainImage.hasAttribute('srcset')) {
                // Generate responsive srcset for variant image
                const srcset = this.generateResponsiveSrcset(imageUrl);
                mainImage.srcset = srcset;
            }

            window.log('info', 'ProductCard', 'Variant image updated', {
                variantId: variant.id,
                imageUrl
            });
        } catch (error) {
            window.log('error', 'ProductCard', 'Failed to update variant image', {
                variantId: variant.id,
                error: error.message
            });
        }
    },

    generateResponsiveSrcset(originalUrl) {
        if (!originalUrl || typeof originalUrl !== 'string') {
            return '';
        }

        // Common responsive breakpoints
        const breakpoints = [200, 300, 400, 500, 600, 700, 800, 900, 1000];

        return breakpoints
            .map(width => `${this.getShopifyImageUrl(originalUrl, width)} ${width}w`)
            .join(', ');
    },

    getShopifyImageUrl(originalUrl, width) {
        if (!originalUrl || typeof originalUrl !== 'string') {
            return originalUrl;
        }

        try {
            // Remove any existing size parameters
            let cleanUrl = originalUrl.replace(/_\d+x\d*\./g, '.');

            // Handle URLs with query parameters (like v= version)
            const [baseUrl, queryString] = cleanUrl.split('?');

            // Add width parameter before file extension
            const sizedUrl = baseUrl.replace(/(\.[^.]*$)/, `_${width}x$1`);

            // Reattach query string if it exists
            return queryString ? `${sizedUrl}?${queryString}` : sizedUrl;
        } catch (error) {
            window.log('warn', 'ProductCard', 'Failed to resize image URL, using original', {
                originalUrl,
                error: error.message
            });
            return originalUrl;
        }
    },

    updateQuantityMax(form, variant) {
        const card = form.closest(CARD_CONFIG.selectors.productCard);
        if (!card) return;

        const quantityInput = card.querySelector(CARD_CONFIG.selectors.quantityInput);
        if (!quantityInput) return;

        let maxQty = 9999;

        if (variant.inventory_management &&
            variant.inventory_policy !== 'continue' &&
            variant.inventory_quantity > 0) {
            maxQty = variant.inventory_quantity;
        }

        quantityInput.max = maxQty;
        quantityInput.dataset.variantInventory = maxQty;

        // Reset quantity if it exceeds max
        const currentQty = parseInt(quantityInput.value, 10);
        if (currentQty > maxQty) {
            quantityInput.value = maxQty;
        }

        // Update button states
        QuantityManager.updateButtonStates(quantityInput);
    },

    handleVariantChange(form) {
        // Find the card containing this form
        const card = form.closest(CARD_CONFIG.selectors.productCard);
        if (!card) {
            window.log('error', 'ProductCard', 'Cannot find product card for form');
            return;
        }

        // Get product data from this specific card
        const productData = ProductCardData.getFromCard(card);
        if (!productData) {
            window.log('warn', 'ProductCard', 'No product data for variant change');
            return;
        }

        const selectedOptions = this.getSelectedOptions(form);
        const matchedVariant = this.findMatchingVariant(productData, selectedOptions);

        if (matchedVariant) {
            this.updateVariantId(form, matchedVariant.id);
            this.updateButton(form, matchedVariant);
            this.updateQuantityMax(form, matchedVariant);
            this.updateVariantImage(form, matchedVariant);

            window.log('info', 'ProductCard', 'Variant changed successfully', {
                productId: productData.id,
                variantId: matchedVariant.id,
                available: matchedVariant.available,
                selectedOptions
            });
        } else {
            window.log('warn', 'ProductCard', 'No variant matched', {
                productId: productData.id,
                selectedOptions,
                availableVariantsCount: productData.variants.length
            });

            // Disable button if no variant matches
            this.updateButton(form, { available: false });
        }
    }
};

/*********************************************************************************************
 * 🔢 QUANTITY MANAGEMENT
 */
const QuantityManager = {
    updateButtonStates(input) {
        if (!input) return;

        const currentValue = parseInt(input.value, 10) || 1;
        const maxValue = parseInt(input.max, 10) || 9999;

        const card = input.closest(CARD_CONFIG.selectors.productCard);
        if (!card) return;

        // Find plus button within the same card
        const plusBtn = card.querySelector(CARD_CONFIG.selectors.quantityIncrease);

        // Update plus button state only (minus button always enabled)
        if (plusBtn) {
            if (currentValue >= maxValue) {
                plusBtn.setAttribute('disabled', '');
            } else {
                plusBtn.removeAttribute('disabled');
            }
        }
    },

    changeQuantity(delta, button) {
        // Find the card container first, then find input within that card
        const card = button.closest(CARD_CONFIG.selectors.productCard);
        if (!card) {
            window.log('warn', 'ProductCard', 'Product card not found', { button });
            return;
        }

        const input = card.querySelector(CARD_CONFIG.selectors.quantityInput);
        if (!input) {
            window.log('warn', 'ProductCard', 'Quantity input not found in card', { card });
            return;
        }

        const current = parseInt(input.value, 10) || 1;
        const min = parseInt(input.min, 10) || 1;
        const max = parseInt(input.max, 10) || 9999;

        // Calculate new value with constraints (same logic as mm_main_product.js)
        const newValue = Math.max(min, Math.min(max, current + delta));

        if (newValue !== current) {
            input.value = newValue;

            // Dispatch change event for potential listeners (consistent with mm_main_product.js)
            input.dispatchEvent(new Event('change', { bubbles: true }));

            this.updateButtonStates(input);

            window.log('info', 'ProductCard', 'Quantity changed', {
                oldValue: current,
                newValue,
                delta
            });
        }
    },

    validateInput(input) {
        if (!input) return;

        let value = parseInt(input.value, 10);
        const min = parseInt(input.min, 10) || 1;
        const max = parseInt(input.max, 10) || 9999;

        if (isNaN(value) || value < min) {
            value = min;
        } else if (value > max) {
            value = max;
        }

        input.value = value;
        this.updateButtonStates(input);
    }
};

/*********************************************************************************************
 * 🎬 EVENT DELEGATION & INITIALIZATION
 */
const ProductCardManager = {
    initialized: false,

    init() {
        // Initialize button states for all quantity inputs
        const quantityInputs = document.querySelectorAll(CARD_CONFIG.selectors.quantityInput);
        quantityInputs.forEach(input => {
            QuantityManager.updateButtonStates(input);
        });

        // Only add event listeners once (use event delegation on document)
        if (!this.initialized) {
            this.setupEventListeners();
            this.initialized = true;
        }

        window.log('info', 'ProductCard', 'Product card manager initialized', {
            formsCount: document.querySelectorAll(CARD_CONFIG.selectors.cardForm).length,
            quantityInputsCount: quantityInputs.length
        });
    },

    setupEventListeners() {
        // Ferme toute pop-in variantes ouverte + deverrouille le scroll (clic exterieur, Escape)
        const closeAllPanels = () => {
            const openPanels = document.querySelectorAll(`${CARD_CONFIG.selectors.variantPanel}.mm-active`);
            if (!openPanels.length) return;
            openPanels.forEach(panel => {
                panel.classList.remove('mm-active');
                const card = panel.closest(CARD_CONFIG.selectors.productCard);
                if (card) card.classList.remove('mm-panel-open');
                // Sans transition (prefers-reduced-motion), transitionend n'est jamais emis
                if (!parseFloat(getComputedStyle(panel).transitionDuration)) {
                    panel.hidden = true;
                    return;
                }
                panel.addEventListener('transitionend', () => {
                    if (!panel.classList.contains('mm-active')) panel.hidden = true;
                }, { once: true });
            });
            document.documentElement.classList.remove('stop-scrolling');
        };

        // Event delegation for quantity buttons (MUST be before stopPropagation listener)
        document.addEventListener('click', (e) => {
            // Handle decrease buttons
            const decreaseBtn = e.target.closest(CARD_CONFIG.selectors.quantityDecrease);
            if (decreaseBtn) {
                e.preventDefault();
                e.stopPropagation();
                window.log('info', 'ProductCard', 'Decrease button clicked', { button: decreaseBtn });
                QuantityManager.changeQuantity(-1, decreaseBtn);
                return;
            }

            // Handle increase buttons
            const increaseBtn = e.target.closest(CARD_CONFIG.selectors.quantityIncrease);
            if (increaseBtn) {
                e.preventDefault();
                e.stopPropagation();
                window.log('info', 'ProductCard', 'Increase button clicked', { button: increaseBtn });
                QuantityManager.changeQuantity(1, increaseBtn);
                return;
            }

            // Bouton croix de la bottom-sheet → ferme
            if (e.target.closest('[data-close-variant-panel]')) {
                e.preventDefault();
                e.stopPropagation();
                closeAllPanels();
                return;
            }

            // Intercept hover CTA click on products with variants → open variant panel
            const openPanelBtn = e.target.closest(CARD_CONFIG.selectors.openVariantPanel);
            if (openPanelBtn) {
                // Au-dessus de 768px (pop-in en bottom-sheet mobile uniquement), ou carte sans pop-in (minimal) :
                // page produit pour faire le choix.
                // Le "+" est un <a href> (navigation native) ; le CTA hover est un <button> (data-product-url).
                const card = openPanelBtn.closest(CARD_CONFIG.selectors.productCard);
                const panel = card && card.querySelector(CARD_CONFIG.selectors.variantPanel);
                if (!panel || !window.isMobile()) {
                    if (openPanelBtn.tagName !== 'A' && openPanelBtn.dataset.productUrl) {
                        e.preventDefault();
                        window.location.href = openPanelBtn.dataset.productUrl;
                    }
                    return;
                }
                e.preventDefault();
                e.stopPropagation();
                panel.hidden = false;
                requestAnimationFrame(() => {
                    panel.classList.add('mm-active');
                    card.classList.add('mm-panel-open');
                });
                // Bottom-sheet mobile : verrouille le scroll de page
                if (window.isMobile && window.isMobile()) {
                    document.documentElement.classList.add('stop-scrolling');
                }
                return;
            }

            // Empeche les clics sur les selecteurs variante de declencher le lien parent
            const variantLabel = e.target.closest('.mm-card-variant-option');
            if (variantLabel) {
                e.stopPropagation();
                return;
            }

            // Ferme la pop-in variantes au clic en dehors du panneau/CTA (incl. overlay flou mobile)
            if (!e.target.closest('a')
                && !e.target.closest(CARD_CONFIG.selectors.variantPanel)
                && !e.target.closest(CARD_CONFIG.selectors.openVariantPanel)) {
                closeAllPanels();
            }
        });

        // Escape ferme la pop-in / bottom-sheet (a11y)
        document.addEventListener('keydown', (e) => {
            // Escape deja consomme par une liste custom ouverte (mm_product_dropdown.js) : on garde la pop-in
            if (e.key === 'Escape' && !e.defaultPrevented) closeAllPanels();
        });

        // Ajout au panier reussi -> ferme la bottom-sheet (le drawer panier prend le relais)
        document.addEventListener('cart:add-to-cart-success', () => closeAllPanels());

        // Event delegation for variant changes
        document.addEventListener('change', (e) => {
            // Color select : swap card content with related product (no page nav)
            const colorSelect = e.target.closest('[data-color-select]');
            if (colorSelect && colorSelect.value) {
                ProductCardSwap.swap(colorSelect);
                return;
            }

            const variantInput = e.target.closest(CARD_CONFIG.selectors.variantInput);
            if (variantInput) {
                const form = variantInput.closest(CARD_CONFIG.selectors.cardForm);
                if (form) {
                    // Update visible text for native <select> rendered in card panel
                    if (variantInput.tagName === 'SELECT') {
                        const valueDisplay = variantInput.parentElement.querySelector('[data-option-value]');
                        if (valueDisplay) valueDisplay.textContent = variantInput.value;
                    } else {
                        // Radio inputs : toggle .mm-selected on associated label
                        const optionName = variantInput.name;
                        const allInputsInGroup = form.querySelectorAll(`input[name="${optionName}"]`);
                        allInputsInGroup.forEach(input => {
                            const label = input.nextElementSibling;
                            if (label && label.tagName === 'LABEL') {
                                label.classList.toggle('mm-selected', input.checked);
                            }
                        });
                    }

                    VariantManager.handleVariantChange(form);
                } else {
                    window.log('error', 'ProductCard', 'Form not found for variant input');
                }
            }
        });

        // Event delegation for quantity input changes (sanitize and validate)
        document.addEventListener('input', (e) => {
            const quantityInput = e.target.closest(CARD_CONFIG.selectors.quantityInput);
            if (quantityInput) {
                // Remove non-numeric characters first
                quantityInput.value = quantityInput.value.replace(/[^0-9]/g, '');

                // Then validate and clamp value
                QuantityManager.validateInput(quantityInput);
            }
        });
    }
};

/*********************************************************************************************
 * 🎨 COLOR SWATCH HOVER — preview related product image without committing
 */
const ColorSwatchPreview = {
    init() {
        document.addEventListener('pointerover', this._onEnter);
        document.addEventListener('pointerout', this._onLeave);
        document.addEventListener('focusin', this._onEnter);
        document.addEventListener('focusout', this._onLeave);
    },

    _getImg(swatch) {
        const card = swatch.closest('.mm-product-card');
        if (!card) return null;
        // Mode editorial (grille collection 3/4 col) : l'image native est masquee,
        // c'est l'image editorial de gauche qui est visible → la cibler en priorite.
        // offsetParent null = element non rendu (display:none sur le .mm-card-link natif).
        const editorial = card.querySelector('img.mm-card-feature__editorial-image');
        if (editorial && editorial.offsetParent !== null) return editorial;
        return card.querySelector('img.mm-product-card-image-main, img.mm-product-card-image');
    },

    _onEnter(e) {
        const swatch = e.target.closest && e.target.closest('[data-preview-image]');
        if (!swatch) return;
        const img = ColorSwatchPreview._getImg(swatch);
        if (!img) return;
        if (!img.dataset.originalSrc) {
            img.dataset.originalSrc = img.currentSrc || img.src;
            img.dataset.originalSrcset = img.srcset || '';
        }
        const card = swatch.closest('.mm-product-card');
        if (card) card.classList.add('mm-swatch-preview');

        const nextSrc = swatch.dataset.previewImage;
        // Deja affichee : rien a faire (evite un rechargement inutile si on repasse dessus)
        if (img.src === nextSrc) return;

        // Swap net (pas de fondu). On precharge/decode d'abord pour eviter le flash blanc,
        // et un token garde la main sur le dernier survol (evite qu'un chargement lent d'un
        // swatch precedent n'ecrase le swatch courant).
        const token = (ColorSwatchPreview._token || 0) + 1;
        ColorSwatchPreview._token = token;

        const next = new Image();
        next.src = nextSrc;
        const apply = () => {
            if (ColorSwatchPreview._token !== token) return;
            img.removeAttribute('srcset');
            img.src = nextSrc;
        };
        if (next.decode) {
            next.decode().then(apply).catch(apply);
        } else {
            apply();
        }
    },

    _onLeave(e) {
        const swatch = e.target.closest && e.target.closest('[data-preview-image]');
        if (!swatch) return;
        const img = ColorSwatchPreview._getImg(swatch);
        const card = swatch.closest('.mm-product-card');
        if (card) card.classList.remove('mm-swatch-preview');
        if (!img || !img.dataset.originalSrc) return;
        img.src = img.dataset.originalSrc;
        if (img.dataset.originalSrcset) img.srcset = img.dataset.originalSrcset;
    },
};

/*********************************************************************************************
 * 🔄 CARD SWAP — fetch alternate product view and replace card in-place
 */
const ProductCardSwap = {
    cache: new Map(),
    inflight: new Map(),

    async swap(select) {
        const url = select.value;
        const card = select.closest('.mm-product-card');
        if (!card || !url) return;

        // Si le panneau variantes etait ouvert (modale couleur), le rouvrir apres swap
        const panelWasOpen = card.classList.contains('mm-panel-open');

        // Lock current dimensions to prevent layout shift during async swap
        const rect = card.getBoundingClientRect();
        const lockedWidth = rect.width;
        const lockedHeight = rect.height;

        try {
            card.classList.add('mm-card-loading');
            const html = await this._fetch(url);
            const newCard = new DOMParser()
                .parseFromString(html, 'text/html')
                .querySelector('.mm-product-card');
            if (!newCard) {
                window.log('error', 'CardSwap', 'No .mm-product-card in fetched HTML', { url });
                return;
            }

            // Apply locked dimensions on new card to keep grid stable
            newCard.style.minHeight = `${lockedHeight}px`;
            newCard.style.minWidth = `${lockedWidth}px`;

            // Preload featured image before insertion (no flash, no reflow)
            const featuredImg = newCard.querySelector('.mm-product-card-image-main, .mm-product-card-image');
            if (featuredImg && featuredImg.tagName === 'IMG' && featuredImg.src) {
                try {
                    const preload = new Image();
                    preload.src = featuredImg.src;
                    if (preload.decode) await preload.decode();
                } catch (_) { /* decoding rejection is non-blocking */ }
            }

            // Reouvrir le panneau AVANT insertion (pas de re-slide, pas de flash du fond)
            if (panelWasOpen) {
                const newPanel = newCard.querySelector('[data-variant-panel]');
                if (newPanel) {
                    newPanel.hidden = false;
                    newCard.classList.add('mm-panel-open');
                    newPanel.classList.add('mm-no-transition', 'mm-active');
                }
            }

            card.replaceWith(newCard);

            // Retablir la transition apres le 1er paint (pour les fermetures ulterieures)
            if (panelWasOpen) {
                const newPanel = newCard.querySelector('[data-variant-panel]');
                requestAnimationFrame(() => newPanel && newPanel.classList.remove('mm-no-transition'));
            }

            // Re-attache le handler AJAX d'ajout panier au nouveau form (sinon submit natif -> /cart)
            if (typeof window.initAddToCartForms === 'function') {
                window.initAddToCartForms();
            }

            // Release the height lock on next frame (image is now painted)
            requestAnimationFrame(() => {
                newCard.style.minHeight = '';
                newCard.style.minWidth = '';
            });

            window.emit('global:card-swapped', { url });
        } catch (err) {
            window.log('error', 'CardSwap', 'Swap failed', { url, error: err.message });
            card.classList.remove('mm-card-loading');
            window.location.href = url;
        }
    },

    async _fetch(url) {
        const key = url + '?view=card';
        if (this.cache.has(key)) return this.cache.get(key);
        if (this.inflight.has(key)) return this.inflight.get(key);

        const promise = fetch(key, { credentials: 'same-origin' })
            .then(res => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.text();
            })
            .then(html => {
                this.cache.set(key, html);
                this.inflight.delete(key);
                return html;
            })
            .catch(err => {
                this.inflight.delete(key);
                throw err;
            });

        this.inflight.set(key, promise);
        return promise;
    },
};

/*********************************************************************************************
 * 🚀 AUTO-INIT
 */
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        ProductCardManager.init();
        ColorSwatchPreview.init();
    });
} else {
    ProductCardManager.init();
    ColorSwatchPreview.init();
}

// Shopify sections: Reinitialize when sections are loaded/reloaded
document.addEventListener('shopify:section:load', (event) => {
    window.log('info', 'ProductCard', 'Shopify section loaded', {
        sectionId: event.detail?.sectionId
    });
    ProductCardManager.init();
});

// Shopify sections: Reinitialize when blocks are loaded
document.addEventListener('shopify:block:select', (event) => {
    window.log('info', 'ProductCard', 'Shopify block selected', {
        blockId: event.detail?.blockId
    });
    ProductCardManager.init();
});

// Export for external use
window.ProductCardManager = ProductCardManager;
